import { TreeSelect } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { useBusinessUnitStore } from '../../../contexts/mobx';
import {
    SELECT_ALL_VALUE,
    useConstructTreeData,
} from './hooks/use-construct-tree-data';
import style from '../shared/style.module.scss';

interface BusinessUnitsSelectTreeProps {
    value?: number[];
    onChange?: (value: number[]) => void;
}

export const BusinessUnitsSelectTree: React.FC<BusinessUnitsSelectTreeProps> = observer(
    ({ value = [], onChange }) => {
        const businessUnitStore = useBusinessUnitStore();

        useEffect(() => {
            businessUnitStore.fetchBusinessUnits();
        }, [businessUnitStore]);

        const {
            treeData,
            allProvinceIds,
            businessUnitToProvincesMap,
        } = useConstructTreeData({
            businessUnitStore,
        });

        // Convert numeric province IDs to string values for TreeSelect
        const treeSelectValue = React.useMemo(() => {
            if (value.length === 0) return [];
            const hasSelectAll = value.includes(
                (SELECT_ALL_VALUE as unknown) as number
            );
            const allSelected =
                allProvinceIds.length > 0 &&
                allProvinceIds.every((id) => value.includes(id));
            if (hasSelectAll || allSelected) {
                return [
                    SELECT_ALL_VALUE,
                    ...allProvinceIds.map((id) => `province-${id}`),
                ];
            }
            return value.map((id) => `province-${id}`);
        }, [value, allProvinceIds]);

        const expandToProvinceIds = (vals: (string | number)[]): number[] => {
            const ids = new Set<number>();
            vals.forEach((val) => {
                if (typeof val === 'string') {
                    if (val.startsWith('province-')) {
                        ids.add(parseInt(val.replace('province-', ''), 10));
                    } else if (val.startsWith('bu-')) {
                        const buId = parseInt(val.replace('bu-', ''), 10);
                        businessUnitToProvincesMap
                            .get(buId)
                            ?.forEach((id) => ids.add(id));
                    }
                }
            });
            return Array.from(ids);
        };

        const handleChange = (newValue: (string | number)[] | undefined) => {
            if (!newValue) {
                onChange?.([]);
                return;
            }

            const hasSelectAll = newValue.includes(SELECT_ALL_VALUE);
            const hadSelectAll =
                value.includes((SELECT_ALL_VALUE as unknown) as number) ||
                (allProvinceIds.length > 0 &&
                    allProvinceIds.every((id) => value.includes(id)));

            if (hasSelectAll && !hadSelectAll) {
                onChange?.([
                    (SELECT_ALL_VALUE as unknown) as number,
                    ...allProvinceIds,
                ]);
            } else if (!hasSelectAll && hadSelectAll) {
                onChange?.([]);
            } else {
                const provinceIds = expandToProvinceIds(newValue);
                if (
                    hasSelectAll ||
                    provinceIds.length === allProvinceIds.length
                ) {
                    onChange?.([
                        (SELECT_ALL_VALUE as unknown) as number,
                        ...provinceIds,
                    ]);
                } else {
                    onChange?.(provinceIds);
                }
            }
        };

        return (
            <TreeSelect
                treeData={treeData}
                placeholder="Select a business unit"
                value={treeSelectValue}
                onChange={handleChange}
                treeCheckable={true}
                showCheckedStrategy="SHOW_PARENT"
                treeDefaultExpandAll={true}
                loading={businessUnitStore.isFetchingBusinessUnits}
                disabled={
                    !businessUnitStore.businessUnits ||
                    businessUnitStore.isFetchingBusinessUnits
                }
                className={style.custom_input}
                popupClassName={style.custom_input}
                allowClear
            />
        );
    }
);
